import {
  AfterViewInit,
  Component,
  HostBinding,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ContextService,
  GlobalStore,
  LoginType,
  OnPremLoginDetails,
  SessionStorageService,
  SystemService,
} from '@maximizer/core/shared/data-access';
import {
  GlobalServicesLoginDetails,
  OAuth2Token,
} from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import { UserSyncConfiguration, UserSyncConfigurationService } from '@maximizer/outlook-sync/data-access';
import {
  AbEntryService,
  IdentityService,
  OnboardingStore,
  OutlookSessionService,
  OutlookStore,
  PreferencesService,
  VerticalService,
  InitializationService,
} from '@maximizer/outlook/shared/data-access';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { normalizeError } from '@maximizer/shared/util';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { catchError, finalize, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'maximizer-outlook-login',
  templateUrl: './login.component.html',
  imports: [KENDO_BUTTON, OUTLOOK_SHARED_UI, TranslatePipe],
})
export class OutlookLoginComponent implements OnInit, AfterViewInit {
  @HostBinding('class.max-outlook') hostClass = true;

  @ViewChild('notification')
  notification!: OutlookNotificationComponent;

  private readonly errorOffice = 'office';

  LoginType = LoginType;
  url = globalThis.location.origin;
  appVersion?: string;
  isLoggedIn = false;
  retryCount = 0;

  constructor(
    private readonly contextService: ContextService,
    private readonly router: Router,
    private readonly session: OutlookSessionService,
    private readonly sessionStorage: SessionStorageService,
    private readonly insights: InsightsService,
    private readonly oauthService: OAuth2Service,
    private readonly activatedRoute: ActivatedRoute,
    private readonly systemService: SystemService,
    private readonly outlookStore: OutlookStore,
    private readonly identityService: IdentityService,
    private readonly globalStore: GlobalStore,
    private readonly outlookSyncService: UserSyncConfigurationService,
    private readonly preferencesService: PreferencesService,
    private readonly abEntryService: AbEntryService,
    private readonly insightsService: InsightsService,
    private readonly verticalService: VerticalService,
    public readonly viewContainerRef: ViewContainerRef,
    private readonly initializationService: InitializationService,
    public onboardingStore: OnboardingStore,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.retryCount = params['retry'] ?? 0;
    });
    this.verifyIfAlreadyLoggedIn()
      .then(() => {
        this.appVersion = this.contextService.version;
        this.sessionStorage.set('url', this.url);

        this.insights.trackPageView({
          name: 'outlook-version',
          pageType: Office.context.platform?.toString(),
        });
      })
      .catch((error) => {
        this.logError('initialization-fail', error);
        this.appVersion = this.contextService.version;
        this.sessionStorage.set('url', this.url);
      });
  }

  ngAfterViewInit(): void {
    if (this.isLoggedIn) return;

    this.handleErrorDisplay();

    this.activatedRoute.queryParams.subscribe((params) => {
      const error = params['error'];
      if (error === this.errorOffice && this.retryCount <= 1) {
        this.openDialog(LoginType.Cloud);
      }
    });
  }

  private handleErrorDisplay(): void {
    const storedAuthError = sessionStorage.getItem('authError');
    if (storedAuthError) {
      this.showError(this.mapErrorToMessage(storedAuthError));
      sessionStorage.removeItem('authError');
      return;
    }

    this.activatedRoute.queryParams.subscribe((params) => {
      const error = params['error'];
      if (error) {
        this.showError(this.mapErrorToMessage(error));
        this.clearErrorQueryParams();
      }
    });
  }

  private showError(message: string): void {
    if (this.notification) {
      this.notification.showMessage('error', message, true);
    }
  }

  private mapErrorToMessage(errorCode: string): string {
    switch (errorCode) {
      case 'loginDetails':
        return 'outlook.auth.error.loginDetailsFailed';
      case 'session-failed':
      case 'auth':
      case 'context-population-failed':
      case 'token-refresh-failed':
      case 'force-logout-failed':
        return 'outlook.auth.error.authFailed';
      case this.errorOffice:
        return 'outlook.auth.error.defaultMessage';
      default:
        return 'outlook.auth.error.authFailed';
    }
  }

  private clearErrorQueryParams(): void {
    this.router.navigate([], { queryParams: {} });
  }

  async openDialog(type: LoginType): Promise<void> {
    const isCloud = type !== LoginType.OnPrem;
    localStorage.setItem('isCloud', isCloud ? 'true' : 'false');
    this.outlookStore.setIsCloud(isCloud);
    this.contextService.isCloud = isCloud;

    if (isCloud) {
      this.insights.enable();
    } else {
      this.insights.disable();
    }

    try {
      if (Office.context.ui) {
        this.trackEvent('login');
        this.openOutlookLogin(type);
      } else {
        if (this.retryCount > 0) {
          this.retryCount++;
          this.notification.showMessage(
            'error',
            'outlook.auth.error.defaultMessage',
            true,
          );
          return;
        }
        this.retryCount++;
        globalThis.location.href = `${this.url}/login?error=${this.errorOffice}&retry=${this.retryCount}`;
      }
    } catch (error) {
      this.logError('open-dialog-fail', error);
    }
  }

  processMessage(arg: { message: string; origin: string | undefined }): void {
    const messageFromDialog = JSON.parse(arg.message);
    const messageObjFromDialog = messageFromDialog as {
      messageType: string;
      auth: OAuth2Token;
      db: GlobalServicesLoginDetails | OnPremLoginDetails;
      email: string;
    };

    const isCloud = messageFromDialog.isCloud ?? true;

    if (messageObjFromDialog.messageType === 'dialogClosed') {
      this.notification.showMessage(
        'error',
        'outlook.auth.error.defaultMessage',
        true,
      );
      return;
    }
    if (messageObjFromDialog.messageType === 'success') {
      this.processSuccessful(
        messageObjFromDialog.db,
        messageObjFromDialog.auth,
        isCloud,
      );
    }
  }

  openLink(type: string): void {
    let link = '';
    switch (type) {
      case 'signup':
        link = 'https://www.maximizer.com/book-a-demo/';
        break;
      case 'terms':
        link = 'https://www.maximizer.com/about-us/terms-of-use/';
        break;
      case 'policy':
        link = 'https://www.maximizer.com/about-us/privacy-policy/';
        break;
      default:
        break;
    }

    this.trackEvent(type);
    window.open(link, '_blank', 'noopener, noreferrer');
  }

  private trackEvent(
    event: string,
    click = true,
    customProperties?: { alias: string; userId: string },
  ): void {
    let eventName = event;
    if (click) {
      eventName = 'Click-' + event;
    }
    this.insights.trackEvent(
      { name: eventName },
      SeverityLevel.Information,
      customProperties,
    );
    this.insights.flush();
  }

  private async verifyIfAlreadyLoggedIn(): Promise<void> {
    try {
      const officeEmail =
        Office.context.mailbox?.userProfile?.emailAddress?.toLocaleLowerCase() ??
        '';

      this.oauthService.storagePartitionKey =
        Office.context.partitionKey ?? officeEmail;
      this.oauthService.encryptPassword = officeEmail;

      const hasStoredCredentials =
        await this.oauthService.populateFromStorage();

      if (hasStoredCredentials) {
        this.isLoggedIn = true;
        this.router.navigate(['/home']);
      } else {
        this.onboardingStore.setOnboardingInProgress(false);
      }
    } catch (error) {
      this.logError('verify-login-fail', error);
      this.onboardingStore.setOnboardingInProgress(false);
    }
  }

  private openOutlookLogin(type: LoginType): void {
    const officeEmail =
      Office.context.mailbox?.userProfile?.emailAddress?.toLocaleLowerCase() ??
      '';

    Office.context.ui?.displayDialogAsync(
      `${this.url}/login-email?email=${encodeURIComponent(officeEmail)}&type=${type}`,
      { height: 60, width: 30 },
      (asyncResult) => {
        const dialog = asyncResult.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg) => {
            dialog.close();
            this.processMessage(
              arg as { message: string; origin: string | undefined },
            );
          },
        );
      },
    );
  }

  private processSuccessful(
    loginDetails: GlobalServicesLoginDetails | OnPremLoginDetails,
    oauth2Token: OAuth2Token,
    isCloud = true,
  ): void {
    this.initializationService.startInitialization();

    const officeEmail =
      Office.context.mailbox?.userProfile?.emailAddress?.toLocaleLowerCase() ??
      '';
    this.oauthService.oAuth2Token = oauth2Token;

    if (isCloud) {
      this.oauthService.cloudLoginDetails =
        loginDetails as GlobalServicesLoginDetails;
    } else {
      this.oauthService.onPremLoginDetails = loginDetails as OnPremLoginDetails;
    }

    this.oauthService.populateContextOAuthData(isCloud);

    // Clear any existing session cache on new login
    this.session.clearSessionCache();

    this.session
      .getInfo()
      .pipe(
        catchError((error) => {
          this.insights.trackEvent(
            { name: 'Login-fail' },
            SeverityLevel.Error,
            { error },
          );
          return of(null);
        }),
      )
      .subscribe((data) => {
        let mxUserId = '';
        let mxUserEmail = '';

        if (data) {
          mxUserId = data.user?.id ?? '';
          mxUserEmail =
            data.user?.email1?.email ??
            data.user?.email2?.email ??
            data.user?.email3?.email ??
            '';

          if (!isCloud && this.oauthService.onPremLoginDetails) {
            this.oauthService.onPremLoginDetails.database = data.database;

            const customerId = data.uniqueDatabaseId.split(':')[0];
            this.oauthService.onPremLoginDetails.customerId = customerId;
          }

          this.globalStore.setSession(data);
        }

        this.oauthService.storagePartitionKey =
          Office.context?.partitionKey ?? officeEmail;
        this.oauthService.encryptPassword = officeEmail;
        this.oauthService.populateContextOAuthData(isCloud);
        this.oauthService.setStorageMxUserId(mxUserId);
        this.oauthService.setStorageMxUserEmail(mxUserEmail);

        if (isCloud) {
          if (loginDetails && 'tenant' in loginDetails) {
            this.oauthService.setStorageLoginDetails(loginDetails);
            this.trackEvent('Login-success', false, {
              alias: loginDetails.tenant.alias,
              userId: mxUserId,
            });
          }
        } else if (loginDetails && 'host' in loginDetails) {
          this.oauthService.setStorageOnPremLoginDetails(loginDetails);
        } else {
          this.logError('login-details-missing', 'Login details are missing');
        }

        this.oauthService.setStorageToken(oauth2Token);
        this.outlookStore.setIsAuthorized(true);
        this.initializeSystemProperties();
      });
  }

  private initializeSystemProperties(): void {
    this.verticalService
      .getDatabaseVertical()
      .pipe(
        finalize(() => {
          this.identityService.populateMaximizerIdentity();
          this.getLastContactDatePreferences();
          this.checkSystemInfoForSyncSupport();
        }),
      )
      .subscribe();
  }

  private checkSystemInfoForSyncSupport(): void {
    this.systemService
      .getInfo()
      .pipe(
        map((systemInfo) =>
          systemInfo
            ? this.systemService.validateDbVersionForNewSync(systemInfo)
            : false,
        ),
        catchError((error) => {
          this.logError('system-info-fail', error);
          return of(false);
        }),
      )
      .subscribe((valid) => {
        this.outlookStore.updateVersion(valid);

        if (valid) {
          this.getOutlookSyncConfiguration();
        } else {
          this.initializationService.stopInitialization();
          this.router.navigate(['/home']);
        }
      });
  }

  private getOutlookSyncConfiguration(): void {
    const session = this.globalStore.session();

    if (session && this.outlookStore.isCloud()) {
      this.outlookSyncService
        .getConfiguration(session.user.id, session.workspace)
        .subscribe({
          next: (config) => {
            if (config) {
              this.outlookStore.updateSyncConfiguration(config);
              this.updatePreferences(config);
            }
            this.initializationService.stopInitialization();
            this.router.navigate(['/home']);
          },
          error: () => {
            this.initializationService.stopInitialization();
            this.router.navigate(['/home']);
          },
        });
    } else {
      this.updatePreferences();
      this.initializationService.stopInitialization();
      this.router.navigate(['/home']);
    }
  }

  private updatePreferences(syncConfig?: UserSyncConfiguration): void {
    this.preferencesService
      .getPreferences()
      .pipe(
        switchMap((preferenceExists) => {
          if (preferenceExists) {
            return this.preferencesService.applySaveOnSendDefault(syncConfig);
          }

          const userHasInsertDocumentRights =
            this.globalStore.session()?.user.rights.documents.insert ?? false;
          this.preferencesService.defaultPreferences.saveNow =
            !syncConfig?.enabled && userHasInsertDocumentRights;
          return this.preferencesService.updatePreferences(
            this.preferencesService.defaultPreferences,
          );
        }),
      )
      .subscribe();
  }

  private getLastContactDatePreferences(): void {
    this.abEntryService
      .getLastContactedPreferences()
      .subscribe((preferences) => {
        this.outlookStore.setLastContactDatePreferences(preferences);
      });
  }

  private logError(eventName: string, error: unknown): void {
    const errorMessage = normalizeError(error);
    const errorObj = error instanceof Error ? error : new Error(errorMessage);

    this.insightsService.trackException({
      error: errorObj,
      properties: { component: 'LoginComponent', eventName },
    });
  }
}
